// src/ui/components/EventModal.setlist.js
// Tarjeta "Setlist" de la pestaña Producción del modal de evento. Solo aparece
// en eventos Confirmado y Realizado. El editor (SetlistModal) y el generador
// del Word se cargan al pulsar, no al abrir el evento.
//
// Admin y manager lo definen y editan; los músicos lo ven y lo descargan.

import { actions } from "../../app/state.js";
import { contentStats, isSetlistStage, songsById, validateContent } from "../../domain/setlist.js";
import { getEventForSetlist, getEventSetlist, listTeamSongs } from "../../services/setlistService.js";

function updatedLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

// Abre el setlist de un evento: editor para admin y manager, solo lectura para
// los musicos. Resuelve al cerrarlo; false si no se pudo cargar el modal.
export async function openEventSetlist(eventId, { canEdit = false } = {}) {
  let openSetlistModal;
  try {
    ({ openSetlistModal } = await import("./SetlistModal.js"));
  } catch (err) {
    actions.addToast?.({ type: "error", message: `No se ha podido abrir el setlist: ${err?.message || err}` });
    return false;
  }
  await openSetlistModal({ mode: "event", eventId, readOnly: !canEdit });
  return true;
}

export async function wireSetlistCard(root, ev, { role } = {}) {
  const card = root.querySelector("#event-setlist-card");
  if (!card || !ev?.id) return;
  if (!isSetlistStage(ev.stage)) {
    card.classList.add("hidden");
    return;
  }

  const canEdit = role === "admin" || role === "manager";
  const statusEl = card.querySelector("#event-setlist-status");
  const openBtn = card.querySelector("#event-setlist-open");
  const openLabel = card.querySelector("#event-setlist-open-label");
  const downloadBtn = card.querySelector("#event-setlist-download");
  card.classList.remove("hidden");

  async function refresh() {
    let setlist;
    try {
      setlist = await getEventSetlist(ev.id);
    } catch (err) {
      console.warn("[setlist] tarjeta:", err);
      statusEl.textContent = "No se ha podido cargar el setlist.";
      openBtn.classList.add("hidden");
      downloadBtn.classList.add("hidden");
      return;
    }
    if (setlist) {
      const { songs } = contentStats(setlist.content);
      statusEl.textContent = `${songs} ${songs === 1 ? "canción" : "canciones"} · actualizado el ${updatedLabel(setlist.updated_at)}`;
      openLabel.textContent = canEdit ? "Editar setlist" : "Ver setlist";
    } else {
      statusEl.textContent = canEdit ? "Sin definir. Arranca con el setlist base." : "Aún no está definido.";
      openLabel.textContent = "Definir setlist";
    }
    openBtn.classList.toggle("hidden", !setlist && !canEdit);
    downloadBtn.classList.toggle("hidden", !setlist);
  }

  openBtn.onclick = async () => {
    openBtn.disabled = true;
    try {
      if (await openEventSetlist(ev.id, { canEdit })) refresh();
    } finally {
      openBtn.disabled = false;
    }
  };

  // Descarga directa de lo guardado, sin abrir el editor.
  downloadBtn.onclick = async () => {
    downloadBtn.disabled = true;
    try {
      const [{ downloadSetlistDocx }, event, setlist] = await Promise.all([
        import("../../services/docx/generateSetlistDocx.js"),
        getEventForSetlist(ev.id),
        getEventSetlist(ev.id),
      ]);
      if (!setlist) throw new Error("Este evento todavía no tiene setlist.");
      const songsMap = songsById(await listTeamSongs(event.team_id));
      const problems = validateContent(setlist.content, songsMap);
      if (problems.length) throw new Error(`${problems[0].message} Abre el setlist para corregirlo.`);
      await downloadSetlistDocx({ event, headerNames: setlist.header_names, content: setlist.content, songsMap });
    } catch (err) {
      actions.addToast?.({ type: "error", message: err?.message || String(err) });
    } finally {
      downloadBtn.disabled = false;
    }
  };

  await refresh();
}
